import React, { createContext, useContext, useState, useMemo } from "react";

const STRINGS = {
  en: {
    nav: {
      home: "Home",
      catalog: "Catalog",
      projects: "Projects",
      reviews: "Reviews",
      contact: "Contact",
      admin: "Admin",
    },
    hero: {
      eyebrow: "TRANSFORM YOUR IDEAS INTO REALITY",
      title_1: "ALL POSSIBLE",
      title_2: "3D SOLUTIONS",
      subtitle:
        "From photos to 3D prints. Upload any image, drawing, or design and watch us bring your vision to life with cutting-edge 3D printing technology.",
      cta_primary: "Browse Catalog",
      cta_secondary: "Request a Custom Print",
      stat_1: "Projects delivered",
      stat_2: "Avg. review rating",
      stat_3: "Delivery",
      stat_3_value: "3–5 days",
    },
    catalog: {
      eyebrow: "/ CATALOG",
      title: "Pick a category. Send us your reference.",
      subtitle:
        "Every print is made to order. Tap a category to send your reference image and a short brief.",
      cta: "Request Order",
      whatsapp: "WhatsApp",
      in_stock_badge: "In Stock · Ships Fast",
    },
    categories: {
      people: {
        name: "People Models",
        tag: "Couples · Solo",
        desc: "Detailed figurines from your photos — couples, solo portraits and family keepsakes.",
      },
      animals: {
        name: "Animal Models",
        tag: "Pets · Wildlife",
        desc: "Pet replicas, flexi creatures and articulated animal figures.",
      },
      kids_cartoon: {
        name: "Children's Drawing Models",
        tag: "From Drawing · To 3D",
        desc: "Turn your child's drawing into a real 3D printed character — painted, safe and keepsake-worthy.",
      },
      action_figures: {
        name: "Action Figure Models",
        tag: "Collectors · Poseable",
        desc: "Custom articulated action figures for collectors and cosplay.",
      },
      keychains: {
        name: "Keychain Models",
        tag: "Batch · Branded",
        desc: "Personalized keychains in bulk — great for shops and events.",
      },
      home_objects: {
        name: "Home Decor Models",
        tag: "Decorative · Styled",
        desc: "Decorative planters, sculptures, lamps and statement pieces for your space.",
      },
      household: {
        name: "Utilities Models",
        tag: "Functional · Everyday",
        desc: "Functional utility objects — organizers, tool holders, hooks and replacement parts that work.",
      },
      word_art: {
        name: "Word Art Models",
        tag: "Typography · 3D Lettering",
        desc: "Sculpted typography and 3D lettering pieces — names, quotes and signage in bold dimensional type.",
      },
      custom_specific: {
        name: "Custom Design Models",
        tag: "One-of-a-kind",
        desc: "A specific model from a reference, scan or sketch — anything printable.",
      },
      mothers_day: {
        name: "Mother's Day",
        tag: "Family · Keepsake",
        desc: "Heartfelt, personalized 3D-printed gifts to celebrate mom — family figurines, keychains and bespoke pieces.",
      },
      in_stock: {
        name: "In Stock",
        tag: "Available Now · Ships Fast",
        desc: "Ready-made pieces available right now. Order today, get them within days — no design wait time.",
        announcement_title: "Everybody needs Jesus,",
        announcement_title_2: "Get your Jesus now!",
        announcement_sub: "Jesus Keychains — with you every where you go.",
      },
    },
    styles: {
      label: "Style",
      placeholder: "Choose a style",
      realista: "Realistic",
      funko_pop: "Funko Pop",
      cartoon: "Cartoon",
      pixar: "Pixar",
      anime: "Japanese Anime",
    },
    illustrative: "Illustrative images only",
    order: {
      title: "Request Order",
      subtitle: "Tell us what you want. We reply within 24 hours.",
      pricing_notice: "Prices may vary depending on the level of detail and painting difficulty, as these items are hand-painted. Request a quote via WhatsApp or Email.",
      whatsapp: "WhatsApp",
      contact_email: "Email",
      name: "Your name",
      email: "Email",
      phone: "Phone (optional)",
      description: "Describe your project",
      description_ph:
        "Size, color, deadline, inspiration, links... anything helps.",
      image: "Reference image (optional)",
      image_hint: "PNG/JPG up to 10MB",
      submit: "Submit Request",
      submitting: "Submitting…",
      success: "Request sent! We'll get back to you soon.",
      error: "Something went wrong. Please try again.",
      upload_error: "Upload failed.",
    },
    projects: {
      eyebrow: "/ PROJECTS",
      title: "Recent work",
      subtitle: "A selection of custom pieces we've delivered for our clients.",
      empty: "No projects yet — check back soon.",
    },
    reviews: {
      eyebrow: "/ REVIEWS",
      title: "What clients say",
      subtitle: "Words from people holding their prints.",
      leave: "Leave a Review",
      leave_desc: "Share your experience. Reviews are posted after a short moderation.",
      rname: "Your name",
      rrating: "Rating",
      rcomment: "Your review",
      submit: "Submit review",
      submitting: "Sending…",
      success: "Thanks! Your review is pending approval.",
      empty: "Be the first to leave a review.",
    },
    contact: {
      eyebrow: "/ CONTACT",
      title: "Let's make it.",
      subtitle: "Reach out directly or submit a request from any catalog card.",
      email_label: "Email",
      phone_label: "Phone",
    },
    category_page: {
      back: "Back to catalog",
      gallery_eyebrow: "/ GALLERY",
      gallery_title: "Examples of what we can make",
      gallery_subtitle: "A selection of pieces in this category. Send us your idea and we'll craft something similar — or completely new.",
      cta: "Request a piece like this",
      completed_eyebrow: "/ COMPLETED",
      completed_title: "Recent deliveries",
      whatsapp_cta: "Chat on WhatsApp",
    },
    estimator: {
      eyebrow: "/ PRICE ESTIMATOR",
      title: "Quick estimate",
      subtitle: "An approximate range. Final price is confirmed by WhatsApp or e-mail and depends on detail and painting complexity.",
      size: "Size",
      finish: "Finish",
      sizes: {
        P: "Small (~5 cm)",
        M: "Medium (~10 cm)",
        G: "Large (~15–20 cm)",
      },
      finishes: {
        matte: "Matte",
        glossy: "Glossy",
      },
      range_label: "Estimated range",
      disclaimer: "* Indicative price only. Hand-painted detail can change the final value.",
      confirm_cta: "Confirm with us",
    },
    featured: "Featured",
    footer: {
      quote: "Because with God All is Possible",
      rights: "All rights reserved.",
    },
    admin: {
      title: "Admin Panel",
      login: "Sign in",
      logging: "Signing in…",
      email: "Email",
      password: "Password",
      tabs: { orders: "Orders", reviews: "Reviews", projects: "Projects" },
      logout: "Log out",
      order: {
        status: "Status",
        new: "New",
        in_progress: "In Progress",
        completed: "Completed",
        cancelled: "Cancelled",
        view_image: "View reference",
        delete: "Delete",
      },
      review: {
        pending: "Pending",
        approved: "Approved",
        approve: "Approve",
        delete: "Delete",
      },
      project: {
        add: "Add project",
        title: "Title",
        desc: "Description",
        image: "Image URL",
        category: "Category",
        create: "Create",
        delete: "Delete",
      },
      bad_login: "Invalid credentials.",
    },
    common: { close: "Close", cancel: "Cancel" },
  },
  pt: {
    nav: {
      home: "Início",
      catalog: "Catálogo",
      projects: "Projetos",
      reviews: "Avaliações",
      contact: "Contato",
      admin: "Admin",
    },
    hero: {
      eyebrow: "TRANSFORME SUAS IDEIAS EM REALIDADE",
      title_1: "ALL POSSIBLE",
      title_2: "3D SOLUTIONS",
      subtitle:
        "De fotos a impressões 3D. Envie qualquer imagem, desenho ou design e veja sua visão ganhar vida com tecnologia de impressão 3D de última geração.",
      cta_primary: "Ver Catálogo",
      cta_secondary: "Solicitar Impressão Personalizada",
      stat_1: "Projetos entregues",
      stat_2: "Nota média",
      stat_3: "Entrega",
      stat_3_value: "3–5 dias",
    },
    catalog: {
      eyebrow: "/ CATÁLOGO",
      title: "Escolha uma categoria. Envie sua referência.",
      subtitle:
        "Toda peça é feita sob encomenda. Toque em uma categoria para enviar sua imagem de referência e um breve resumo.",
      cta: "Solicitar Ordem",
      whatsapp: "WhatsApp",
      in_stock_badge: "Em Estoque · Envio Rápido",
    },
    categories: {
      people: {
        name: "Modelos de Pessoas",
        tag: "Casais · Solteiros",
        desc: "Miniaturas detalhadas a partir das suas fotos — casais, retratos e lembranças de família.",
      },
      animals: {
        name: "Modelos de Animais",
        tag: "Pets · Selvagens",
        desc: "Réplicas de pets, criaturas flexíveis e figuras articuladas de animais.",
      },
      kids_cartoon: {
        name: "Modelos de Desenho Infantil",
        tag: "Quarto · PLA seguro",
        desc: "Modelos inspirados em desenhos e anime, pintados e seguros para crianças.",
      },
      action_figures: {
        name: "Modelos Figure Action",
        tag: "Colecionador · Articulado",
        desc: "Figuras articuladas personalizadas para colecionadores e cosplay.",
      },
      keychains: {
        name: "Modelos de Chaveiro",
        tag: "Lote · Personalizado",
        desc: "Chaveiros personalizados em lote — ideais para lojas e eventos.",
      },
      home_objects: {
        name: "Modelos de Decoração",
        tag: "Decorativo · Estilo",
        desc: "Vasos decorativos, esculturas, luminárias e peças de destaque para o seu espaço.",
      },
      household: {
        name: "Modelos Utilitários",
        tag: "Funcional · Dia a dia",
        desc: "Objetos utilitários funcionais — organizadores, suportes de ferramentas, ganchos e peças de reposição que realmente funcionam.",
      },
      word_art: {
        name: "Modelos Word Art",
        tag: "Tipografia · Letras 3D",
        desc: "Tipografia esculpida e letras 3D — nomes, frases e placas em tipografia dimensional em destaque.",
      },
      custom_specific: {
        name: "Modelos Personalizados",
        tag: "Exclusivo",
        desc: "Um modelo específico a partir de referência, scan ou esboço — qualquer coisa imprimível.",
      },
      mothers_day: {
        name: "Dia das Mães",
        tag: "Família · Lembrança",
        desc: "Presentes personalizados em 3D para celebrar a mamãe — miniaturas em família, chaveiros e peças exclusivas.",
      },
      in_stock: {
        name: "Em Estoque",
        tag: "Disponível Agora · Envio Rápido",
        desc: "Peças prontas, disponíveis agora. Compre hoje e receba em poucos dias — sem espera de produção.",
        announcement_title: "Everybody needs Jesus,",
        announcement_title_2: "Get your Jesus now!",
        announcement_sub: "Jesus Keychains — with you every where you go.",
      },
    },
    styles: {
      label: "Estilo",
      placeholder: "Escolha um estilo",
      realista: "Realista",
      funko_pop: "Funko Pop",
      cartoon: "Cartoon",
      pixar: "Pixar",
      anime: "Anime Japonês",
    },
    illustrative: "Imagens meramente ilustrativas",
    order: {
      title: "Solicitar Ordem",
      subtitle: "Conte o que deseja. Respondemos em até 24 horas.",
      pricing_notice: "Os preços podem variar conforme o nível de detalhe e a dificuldade da pintura, pois as peças são pintadas à mão. Solicite um orçamento pelo WhatsApp ou E-mail.",
      whatsapp: "WhatsApp",
      contact_email: "E-mail",
      name: "Seu nome",
      email: "E-mail",
      phone: "Telefone (opcional)",
      description: "Descreva o projeto",
      description_ph:
        "Tamanho, cor, prazo, inspiração, links... tudo ajuda.",
      image: "Imagem de referência (opcional)",
      image_hint: "PNG/JPG até 10MB",
      submit: "Enviar Solicitação",
      submitting: "Enviando…",
      success: "Solicitação enviada! Retornamos em breve.",
      error: "Algo deu errado. Tente novamente.",
      upload_error: "Falha no upload.",
    },
    projects: {
      eyebrow: "/ PROJETOS",
      title: "Trabalhos recentes",
      subtitle: "Uma seleção de peças personalizadas entregues aos clientes.",
      empty: "Ainda sem projetos — volte em breve.",
    },
    reviews: {
      eyebrow: "/ AVALIAÇÕES",
      title: "O que os clientes dizem",
      subtitle: "Palavras de pessoas segurando suas impressões.",
      leave: "Deixar uma avaliação",
      leave_desc:
        "Compartilhe sua experiência. As avaliações aparecem após moderação.",
      rname: "Seu nome",
      rrating: "Nota",
      rcomment: "Sua avaliação",
      submit: "Enviar avaliação",
      submitting: "Enviando…",
      success: "Obrigado! Sua avaliação está aguardando aprovação.",
      empty: "Seja o primeiro a deixar uma avaliação.",
    },
    contact: {
      eyebrow: "/ CONTATO",
      title: "Vamos criar.",
      subtitle:
        "Fale direto com a gente ou envie um pedido por qualquer card do catálogo.",
      email_label: "E-mail",
      phone_label: "Telefone",
    },
    category_page: {
      back: "Voltar ao catálogo",
      gallery_eyebrow: "/ GALERIA",
      gallery_title: "Exemplos do que podemos fazer",
      gallery_subtitle: "Uma seleção de peças nesta categoria. Envie sua ideia e fazemos algo parecido — ou totalmente novo.",
      cta: "Solicitar peça parecida",
      completed_eyebrow: "/ ENTREGUES",
      completed_title: "Entregas recentes",
      whatsapp_cta: "Falar no WhatsApp",
    },
    estimator: {
      eyebrow: "/ ESTIMATIVA DE PREÇO",
      title: "Estimativa rápida",
      subtitle: "Faixa aproximada. O preço final é confirmado pelo WhatsApp ou e-mail e depende do nível de detalhe e da pintura.",
      size: "Tamanho",
      finish: "Acabamento",
      sizes: {
        P: "Pequeno (~5 cm)",
        M: "Médio (~10 cm)",
        G: "Grande (~15–20 cm)",
      },
      finishes: {
        matte: "Fosco (matte)",
        glossy: "Brilhante (glossy)",
      },
      range_label: "Faixa estimada",
      disclaimer: "* Preço apenas indicativo. A pintura à mão pode alterar o valor final.",
      confirm_cta: "Confirmar com a gente",
    },
    featured: "Destaque",
    footer: {
      quote: "Porque com Deus Tudo é possível",
      rights: "Todos os direitos reservados.",
    },
    admin: {
      title: "Painel Admin",
      login: "Entrar",
      logging: "Entrando…",
      email: "E-mail",
      password: "Senha",
      tabs: { orders: "Pedidos", reviews: "Avaliações", projects: "Projetos" },
      logout: "Sair",
      order: {
        status: "Status",
        new: "Novo",
        in_progress: "Em Andamento",
        completed: "Concluído",
        cancelled: "Cancelado",
        view_image: "Ver imagem",
        delete: "Excluir",
      },
      review: {
        pending: "Pendente",
        approved: "Aprovada",
        approve: "Aprovar",
        delete: "Excluir",
      },
      project: {
        add: "Adicionar projeto",
        title: "Título",
        desc: "Descrição",
        image: "URL da imagem",
        category: "Categoria",
        create: "Criar",
        delete: "Excluir",
      },
      bad_login: "Credenciais inválidas.",
    },
    common: { close: "Fechar", cancel: "Cancelar" },
  },
};

const I18nCtx = createContext({ lang: "en", t: STRINGS.en, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("ap3d_lang") || "en";
    } catch {
      return "en";
    }
  });
  const value = useMemo(() => {
    const setLangPersist = (l) => {
      setLang(l);
      try {
        localStorage.setItem("ap3d_lang", l);
      } catch {}
    };
    return { lang, t: STRINGS[lang] || STRINGS.en, setLang: setLangPersist };
  }, [lang]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
